import { Injectable, inject, signal } from '@angular/core';
import { TicketCardItem } from '../../Composants/silo-tickets/silo-tickets';
import { CONFIG_ETATS_TICKET, TicketColumnState } from '../../Services/ticket-state';
import { TicketService } from '../../Services/ticket';
import { DraggedTicket, TicketSilo } from './tickets.types';

@Injectable()
export class TicketDragService {
  private ticketService = inject(TicketService);

  readonly draggingTicket = signal<DraggedTicket | null>(null);
  readonly dropTargetState = signal<TicketColumnState | null>(null);
  readonly invalidDropTargetState = signal<TicketColumnState | null>(null);
  readonly toastMessage = signal<string | null>(null);
  private toastTimeoutId: ReturnType<typeof setTimeout> | null = null;

  onTicketDragStarted(fromState: TicketColumnState, ticket: TicketCardItem) {
    if (!this.peutEtreDeplaceDepuis(fromState)) return;
    this.draggingTicket.set({ fromState, ticket });
  }

  onTicketDragEnded() {
    this.draggingTicket.set(null);
    this.dropTargetState.set(null);
    this.invalidDropTargetState.set(null);
  }

  onSiloDragEntered(state: TicketColumnState) {
    if (!this.draggingTicket()) return;

    if (!this.peutRecevoirDrop(state)) {
      this.invalidDropTargetState.set(state);
      this.dropTargetState.set(null);
      return;
    }

    this.invalidDropTargetState.set(null);
    this.dropTargetState.set(state);
  }

  onSiloDragLeft(state: TicketColumnState) {
    if (this.dropTargetState() === state) this.dropTargetState.set(null);
    if (this.invalidDropTargetState() === state) this.invalidDropTargetState.set(null);
  }

  async onSiloDropped(
    targetState: TicketColumnState,
    silos: TicketSilo[],
  ): Promise<TicketSilo[] | null> {
    const drag = this.draggingTicket();
    if (!drag || !this.peutRecevoirDrop(targetState)) return null;

    const source = silos.find((silo) => silo.state === drag.fromState);
    const target = silos.find((silo) => silo.state === targetState);

    if (!source || !target) {
      this.onTicketDragEnded();
      return null;
    }

    if (!drag.ticket.id) {
      this.afficherToast('Impossible de déplacer ce ticket : identifiant introuvable.');
      this.onTicketDragEnded();
      return null;
    }

    let ticketMisAJour = drag.ticket;
    try {
      const ticketEnregistre = await this.ticketService.updateTicketState(
        drag.ticket.id,
        targetState,
      );
      if (!ticketEnregistre) {
        this.afficherToast(
          `Le ticket n'a pas pu être déplacé vers "${this.libelleEtat(targetState)}".`,
        );
        this.onTicketDragEnded();
        return null;
      }
      ticketMisAJour = {
        ...drag.ticket,
        bonsInterventionNonLus:
          ticketEnregistre.bons_intervention_non_lus ?? drag.ticket.bonsInterventionNonLus,
        vu:
          ticketEnregistre.messages_non_lus === undefined
            ? drag.ticket.vu
            : !ticketEnregistre.messages_non_lus,
      };
    } catch (error) {
      console.error('Erreur mise a jour etat ticket', error);
      this.afficherToast(
        `Le ticket n'a pas pu être déplacé vers "${this.libelleEtat(targetState)}".`,
      );
      this.onTicketDragEnded();
      return null;
    }

    const updatedSilos = silos.map((silo) => {
      if (silo.state === source.state) {
        const tickets = silo.tickets.filter(
          (ticket) => (ticket.id ?? ticket.reference) !== (drag.ticket.id ?? drag.ticket.reference),
        );
        return {
          ...silo,
          count: Math.max(0, silo.count - 1),
          badgeCount: Math.max(0, silo.badgeCount - (drag.ticket.vu ? 0 : 1)),
          tickets,
        };
      }
      if (silo.state === target.state) {
        const tickets = [ticketMisAJour, ...silo.tickets];
        return {
          ...silo,
          count: silo.count + 1,
          badgeCount: silo.badgeCount + (ticketMisAJour.vu ? 0 : 1),
          tickets,
        };
      }
      return silo;
    });

    this.onTicketDragEnded();
    return updatedSilos;
  }

  onSiloInvalidDropped(targetState: TicketColumnState) {
    const drag = this.draggingTicket();
    if (!drag) return;
    this.invalidDropTargetState.set(targetState);
    this.afficherToast(
      `Impossible de déplacer un ticket "${this.libelleEtat(drag.fromState)}" vers "${this.libelleEtat(targetState)}".`,
    );
  }

  peutRecevoirDrop(targetState: TicketColumnState): boolean {
    const drag = this.draggingTicket();
    if (!drag) return false;
    return CONFIG_ETATS_TICKET[drag.fromState].transitionsAutorisees.includes(targetState);
  }

  libelleEtat(state: TicketColumnState): string {
    return CONFIG_ETATS_TICKET[state].label;
  }

  private peutEtreDeplaceDepuis(state: TicketColumnState): boolean {
    return CONFIG_ETATS_TICKET[state].transitionsAutorisees.length > 0;
  }

  private afficherToast(message: string) {
    this.toastMessage.set(message);
    if (this.toastTimeoutId) clearTimeout(this.toastTimeoutId);
    this.toastTimeoutId = setTimeout(() => {
      this.toastMessage.set(null);
      this.toastTimeoutId = null;
    }, 2400);
  }
}
